import React from 'react';

const WEEK_DAYS = [
  { value: '0', label: 'Monday' },
  { value: '1', label: 'Tuesday' },
  { value: '2', label: 'Wednesday' },
  { value: '3', label: 'Thursday' },
  { value: '4', label: 'Friday' },
  { value: '5', label: 'Saturday' },
  { value: '6', label: 'Sunday' },
  { value: 'ph', label: 'Public Holiday' }
];

const defaultSchedule = () =>
  Object.fromEntries(WEEK_DAYS.map((d) => [d.value, []]));

const ensureScheduleShape = (schedule) => {
  if (!schedule || typeof schedule !== 'object') return defaultSchedule();
  const out = { ...defaultSchedule() };
  WEEK_DAYS.forEach((d) => {
    out[d.value] = Array.isArray(schedule[d.value])
      ? schedule[d.value].map((slot) => ({
          f: slot.f ?? slot.from ?? '09:00',
          t: slot.t ?? slot.to ?? '17:00'
        }))
      : [];
  });
  return out;
};

const AgentScheduleSection = ({ clinic, editedClinic, setEditedClinic, isEditing }) => {
  const settings = isEditing ? editedClinic?.settings : clinic?.settings;
  const agentSchedule = settings?.agent_schedule;
  const schedule = ensureScheduleShape(agentSchedule?.schedule);

  const handleFieldChange = (field, value) => {
    if (!isEditing) return;
    setEditedClinic((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        agent_schedule: {
          ...prev.settings?.agent_schedule,
          [field]: value
        }
      }
    }));
  };

  const handleScheduleChange = (newSchedule) => {
    if (!isEditing) return;
    setEditedClinic((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        agent_schedule: {
          ...prev.settings?.agent_schedule,
          schedule: newSchedule
        }
      }
    }));
  };

  const addSlot = (dayValue) => {
    const daySlots = Array.isArray(schedule[dayValue]) ? [...schedule[dayValue]] : [];
    daySlots.push({ f: '09:00', t: '17:00' });
    handleScheduleChange({ ...schedule, [dayValue]: daySlots });
  };

  const removeSlot = (dayValue, slotIndex) => {
    const daySlots = [...(schedule[dayValue] || [])];
    daySlots.splice(slotIndex, 1);
    handleScheduleChange({ ...schedule, [dayValue]: daySlots });
  };

  const updateSlot = (dayValue, slotIndex, field, value) => {
    const daySlots = [...(schedule[dayValue] || [])];
    if (!daySlots[slotIndex]) return;
    daySlots[slotIndex] = { ...daySlots[slotIndex], [field]: value };
    handleScheduleChange({ ...schedule, [dayValue]: daySlots });
  };

  const copyFromDay = (fromDayValue, toDayValue) => {
    const sourceSlots = (schedule[fromDayValue] || []).map((slot) => ({ f: slot.f, t: slot.t }));
    handleScheduleChange({ ...schedule, [toDayValue]: sourceSlots });
  };

  const applyToAllDays = (fromDayValue) => {
    if (!window.confirm("Apply this day's schedule to all other days?")) return;
    const sourceSlots = (schedule[fromDayValue] || []).map((slot) => ({ f: slot.f, t: slot.t }));
    const next = { ...schedule };
    WEEK_DAYS.forEach((d) => {
      if (d.value !== fromDayValue) next[d.value] = [...sourceSlots];
    });
    handleScheduleChange(next);
  };

  const readOnlySchedule = ensureScheduleShape(agentSchedule?.schedule);

  if (!isEditing) {
    const enabled = agentSchedule?.enabled ?? false;
    const useSchedule = agentSchedule?.should_use_schedule ?? false;
    return (
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="text-md font-semibold mb-4">Agent Schedule</h4>
        <p className="text-sm text-gray-600 mb-2">
          Controls when the agent is considered &quot;available&quot; (e.g. for routing or out-of-hours handling).
        </p>
        <p className="flex items-center gap-2 mb-2">
          <span className="font-medium text-gray-700">Use schedule:</span>
          <span className={useSchedule ? 'text-green-700' : 'text-gray-500'}>
            {useSchedule ? 'Yes' : 'No'}
          </span>
        </p>
        {useSchedule && (
          <p className="flex items-center gap-2 mb-4">
            <span className="font-medium text-gray-700">Enabled (manually set):</span>
            <span className={enabled ? 'text-green-700' : 'text-gray-500'}>
              {enabled ? 'Yes' : 'No'}
            </span>
          </p>
        )}
        {useSchedule && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Day</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Time slots</th>
                </tr>
              </thead>
              <tbody>
                {WEEK_DAYS.map((day) => (
                  <tr key={day.value} className="border-b last:border-b-0">
                    <td className="px-3 py-2 font-medium text-gray-700">{day.label}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {readOnlySchedule[day.value]?.length > 0 ? (
                        <span>
                          {readOnlySchedule[day.value].map((slot, i) => (
                            <span key={i}>
                              {i > 0 && ', '}
                              {slot.f} – {slot.t}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span className="text-gray-400">No slots</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-6 pt-6 border-t border-gray-200">
      <h4 className="text-md font-semibold mb-4">Agent Schedule</h4>
      <p className="text-sm text-gray-600 mb-4">
        Configure when the agent is considered available. Used for schedule-based routing and out-of-hours behaviour.
      </p>

      <div className="flex items-start space-x-4 mb-6">
        <div className="flex items-center h-5">
          <input
            id="agent_schedule_should_use"
            type="checkbox"
            checked={agentSchedule?.should_use_schedule ?? false}
            onChange={(e) => handleFieldChange('should_use_schedule', e.target.checked)}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
          />
        </div>
        <div className="ml-3 text-sm">
          <label htmlFor="agent_schedule_should_use" className="font-medium text-gray-700">
            Use weekly schedule
          </label>
          <p className="text-gray-500">
            When enabled, the weekly and public-holiday slots below define when the agent is available.
          </p>
        </div>
      </div>

      {(agentSchedule?.should_use_schedule ?? false) && (
        <div className="flex items-start space-x-4 mb-6 pl-7 border-l-2 border-gray-200">
          <div className="flex items-center h-5">
            <input
              id="agent_schedule_enabled"
              type="checkbox"
              checked={agentSchedule?.enabled ?? false}
              onChange={(e) => handleFieldChange('enabled', e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="agent_schedule_enabled" className="font-medium text-gray-700">
              Agent Enabled
            </label>
            <p className="text-gray-500">
              Check when you have manually set the schedule and want it to take effect.
            </p>
          </div>
        </div>
      )}

      {(agentSchedule?.should_use_schedule ?? false) && (
        <div className="space-y-4">
          <h5 className="text-sm font-medium text-gray-700">Weekly schedule</h5>
          <p className="text-xs text-gray-500 mb-3">
            Set time slots (from–to) for each day. Agent is &quot;available&quot; only within these slots. Use &quot;Public Holiday&quot; for holidays.
          </p>
          <div className="space-y-4">
            {WEEK_DAYS.map((day) => (
              <div key={day.value} className="border rounded-lg overflow-hidden bg-gray-50/50">
                <div className="bg-gray-100 px-3 py-2 font-medium text-gray-700 flex flex-wrap justify-between items-center gap-2">
                  <span>{day.label}</span>
                  <div className="flex gap-2 flex-wrap">
                    <select
                      onChange={(e) => {
                        const fromDay = e.target.value;
                        if (fromDay) copyFromDay(fromDay, day.value);
                        e.target.value = '';
                      }}
                      className="bg-white border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      defaultValue=""
                    >
                      <option value="" disabled>Copy from...</option>
                      {WEEK_DAYS.filter((d) => d.value !== day.value).map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => applyToAllDays(day.value)}
                      className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                      title="Apply this day's schedule to all other days"
                    >
                      Apply to all
                    </button>
                    <button
                      type="button"
                      onClick={() => addSlot(day.value)}
                      className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                    >
                      Add slot
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  {(!schedule[day.value] || schedule[day.value].length === 0) ? (
                    <p className="text-gray-500 text-sm">No time slots. Click &quot;Add slot&quot; to add one.</p>
                  ) : (
                    <div className="space-y-2">
                      {schedule[day.value].map((slot, slotIndex) => (
                        <div key={slotIndex} className="flex items-center gap-3 flex-wrap">
                          <input
                            type="time"
                            value={slot.f}
                            onChange={(e) => updateSlot(day.value, slotIndex, 'f', e.target.value)}
                            className="p-2 border rounded text-sm"
                          />
                          <span className="text-gray-500">to</span>
                          <input
                            type="time"
                            value={slot.t}
                            onChange={(e) => updateSlot(day.value, slotIndex, 't', e.target.value)}
                            className="p-2 border rounded text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => removeSlot(day.value, slotIndex)}
                            className="p-1 text-red-500 hover:text-red-700"
                            title="Remove slot"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentScheduleSection;
